import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Menu, X } from "lucide-react";
import Navbar from "@/components/Navbar";

// Cadre des panneaux (tableau de bord des bots, anti-cheat) au style du panel
// admin : menu latéral (tiroir sur téléphone), en-tête de rubrique, contenu.
//   sections : [{ id, label, hint, icon, dot }]   dot : "violet" | "amber" | "red"
export default function PanelShell({ brand, sections, active, onSelect, back, header, extraNav, children, testid = "panel" }) {
  const [open, setOpen] = useState(false);
  const current = sections.find((s) => s.id === active) || sections[0];
  const dotCls = { violet: "bg-blue-400", amber: "bg-amber-400", red: "bg-red-400" };

  const nav = (onNavigate, prefix) => (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3">{brand}</div>
      {extraNav && <div className="px-4 pb-3">{extraNav}</div>}
      <nav className="flex-1 px-3 pb-3 space-y-0.5" data-testid={`${prefix}-list`}>
        {sections.map((s) => {
          const on = s.id === current?.id;
          const Icon = s.icon;
          return (
            <button key={s.id} type="button" data-testid={`${prefix === `${testid}-nav` ? "tab" : `${prefix}-tab`}-${s.id}`}
              onClick={() => { onSelect(s.id); onNavigate && onNavigate(); }}
              className={`group relative w-full flex items-center gap-3 pl-3 pr-2.5 py-2 text-left rounded-xl transition-colors ${on ? "bg-white/[0.07]" : "hover:bg-white/[0.035]"}`}>
              {on && <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 w-[3px] rounded-r-full bg-gradient-to-b from-blue-400 to-violet-500" />}
              <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 ring-1 ring-inset transition-transform group-hover:scale-105 ${on ? "bg-gradient-to-br from-blue-500/25 to-violet-500/20 ring-blue-400/25" : "bg-white/[0.03] ring-white/[0.05]"}`}>
                <Icon className={`w-4 h-4 ${on ? "text-blue-200" : "text-white/45"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] leading-tight truncate ${on ? "text-white font-medium" : "text-white/70"}`}>{s.label}</p>
                {s.hint && <p className="text-[11px] text-white/30 truncate mt-0.5">{s.hint}</p>}
              </div>
              {s.dot && <span className={`w-2 h-2 rounded-full shrink-0 ${dotCls[s.dot] || dotCls.violet}`} />}
            </button>
          );
        })}
      </nav>
      {back && (
        <div className="px-3 py-3 border-t border-white/5">
          <Link to={back.to} onClick={onNavigate} className="w-full flex items-center gap-3 px-3 py-2 text-[13px] text-white/45 hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4" />{back.label}
          </Link>
        </div>
      )}
    </div>
  );

  const CurrentIcon = current?.icon;
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <div className="pt-16 flex min-h-screen">
        <aside className="hidden lg:block w-64 bg-[#0b0c14]/95 backdrop-blur-xl border-r border-white/[0.06] fixed left-0 top-16 bottom-0 overflow-y-auto z-10" data-testid={`${testid}-sidebar`}>
          {nav(null, `${testid}-nav`)}
        </aside>
        {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setOpen(false)} />}
        <aside className={`fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-[#0b0c14] border-r border-white/[0.06] z-50 overflow-y-auto transition-transform duration-200 lg:hidden ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <button type="button" aria-label="Fermer le menu" onClick={() => setOpen(false)} className="absolute top-5 right-5 p-1.5 hover:bg-white/5 rounded z-10"><X className="w-5 h-5 text-white/50" /></button>
          {nav(() => setOpen(false), `${testid}-mobile`)}
        </aside>
        <main className="lg:ml-64 flex-1 min-w-0 p-4 lg:p-8 pb-28">
          <div className="max-w-6xl">
            <button type="button" data-testid={`${testid}-menu`} onClick={() => setOpen(true)} className="lg:hidden flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-[#0f1019] border border-white/[0.07] text-sm text-white/60 hover:text-white">
              <Menu className="w-4 h-4" />Menu
            </button>
            {header}
            {current && (
              <div key={current.id} className="page-rise flex items-center gap-3.5 mb-5" data-testid={`${testid}-section-header`}>
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 ring-1 ring-inset ring-white/10 flex items-center justify-center shrink-0">
                  {CurrentIcon && <CurrentIcon className="w-5 h-5 text-blue-200" />}
                </div>
                <div className="min-w-0">
                  <h1 className="font-heading text-2xl font-bold tracking-tight truncate">{current.label}</h1>
                  {current.hint && <p className="text-sm text-white/40 truncate">{current.hint}</p>}
                </div>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
